import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiArrowLeft, FiClock, FiTarget, FiCalendar } = FiIcons;

const ProjectScope = ({ data = {}, onUpdate, onComplete }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    targetWordCount: '',
    customWordCount: '',
    chapterCount: '',
    timeline: '',
    customTimeline: '',
    writingDaysPerWeek: 5,
    writingSchedule: '',
    obstacles: '',
    ...data
  });

  useEffect(() => {
    if (onUpdate) {
      onUpdate(formData);
    }
  }, [formData, onUpdate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const wordCountOptions = [
    { value: '20000', label: '20,000 words (Short book)' },
    { value: '30000', label: '30,000 words (Novella)' },
    { value: '50000', label: '50,000 words (Standard)' },
    { value: '70000', label: '70,000 words (Full-length)' },
    { value: 'custom', label: 'Custom word count' }
  ];

  const timelineOptions = [
    { value: '30', label: '30 days (Sprint)' },
    { value: '60', label: '60 days (Focused)' },
    { value: '90', label: '90 days (Steady)' },
    { value: 'custom', label: 'Custom timeline' }
  ];

  const chapterCountOptions = Array.from({ length: 10 }, (_, i) => ({
    value: (i + 6).toString(),
    label: `${i + 6} chapters`
  }));

  const calculateDailyGoal = () => {
    const wordCount = formData.targetWordCount === 'custom' 
      ? parseInt(formData.customWordCount) || 0
      : parseInt(formData.targetWordCount) || 0;
    
    const days = formData.timeline === 'custom'
      ? parseInt(formData.customTimeline) || 0
      : parseInt(formData.timeline) || 0;
    
    const writingDays = Math.floor((days / 7) * formData.writingDaysPerWeek);
    
    if (wordCount && writingDays) {
      return Math.ceil(wordCount / writingDays);
    }
    return 0;
  };

  const getWordsPerChapter = () => {
    const wordCount = formData.targetWordCount === 'custom' 
      ? parseInt(formData.customWordCount) || 0
      : parseInt(formData.targetWordCount) || 0;
    
    const chapters = parseInt(formData.chapterCount) || 0;
    
    if (wordCount && chapters) {
      return Math.ceil(wordCount / chapters);
    }
    return 0;
  };

  const handleContinue = () => {
    if (onComplete) {
      onComplete();
    }
    navigate('/kickstart');
  };

  const handleBack = () => {
    navigate('/structure');
  };

  const dailyGoal = calculateDailyGoal();
  const wordsPerChapter = getWordsPerChapter();
  const isFormComplete = formData.targetWordCount && formData.timeline;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      <div className="text-center mb-8">
        <h2 className="font-serif text-3xl font-bold text-stone-800 mb-3">
          Project Scope
        </h2>
        <p className="text-stone-600 text-lg">
          Let's map out your writing journey with realistic goals and rhythms.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Input Form */}
        <div className="space-y-6">
          <motion.div 
            className="card"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiTarget} className="text-brand-orange mr-2" />
              <h3 className="font-semibold text-stone-800">Target Word Count</h3>
            </div>
            <select
              className="form-select mb-4"
              value={formData.targetWordCount}
              onChange={(e) => handleInputChange('targetWordCount', e.target.value)}
            >
              <option value="">Choose your target...</option>
              {wordCountOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            
            {formData.targetWordCount === 'custom' && (
              <motion.input
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                type="number"
                className="form-input"
                placeholder="Enter custom word count..."
                value={formData.customWordCount}
                onChange={(e) => handleInputChange('customWordCount', e.target.value)}
              />
            )}
          </motion.div>

          <motion.div 
            className="card"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiTarget} className="text-brand-orange mr-2" />
              <h3 className="font-semibold text-stone-800">Chapter Count Estimate</h3>
            </div>
            <select
              className="form-select"
              value={formData.chapterCount}
              onChange={(e) => handleInputChange('chapterCount', e.target.value)}
            >
              <option value="">Choose chapter count...</option>
              {chapterCountOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </motion.div>

          <motion.div 
            className="card"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiCalendar} className="text-brand-blue mr-2" />
              <h3 className="font-semibold text-stone-800">Timeline</h3>
            </div>
            <select
              className="form-select mb-4"
              value={formData.timeline}
              onChange={(e) => handleInputChange('timeline', e.target.value)}
            >
              <option value="">Choose your timeline...</option>
              {timelineOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            
            {formData.timeline === 'custom' && (
              <motion.input
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                type="number"
                className="form-input"
                placeholder="Enter days..."
                value={formData.customTimeline}
                onChange={(e) => handleInputChange('customTimeline', e.target.value)}
              />
            )}
          </motion.div>

          <motion.div 
            className="card"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiClock} className="text-brand-orange mr-2" />
              <h3 className="font-semibold text-stone-800">Writing Days per Week</h3>
            </div>
            <div className="mb-4">
              <input
                type="range"
                min="1"
                max="7"
                value={formData.writingDaysPerWeek}
                onChange={(e) => handleInputChange('writingDaysPerWeek', parseInt(e.target.value))}
                className="w-full h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-sm text-stone-500 mt-2">
                <span>1 day</span>
                <span className="font-medium text-brand-blue">
                  {formData.writingDaysPerWeek} days
                </span>
                <span>7 days</span>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="card"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiClock} className="text-brand-blue mr-2" />
              <h3 className="font-semibold text-stone-800">Writing Schedule</h3>
            </div>
            <textarea
              className="form-textarea"
              placeholder="When will you write? (e.g., 'Every morning at 6 AM' or 'Tuesday/Thursday evenings after dinner')"
              value={formData.writingSchedule}
              onChange={(e) => handleInputChange('writingSchedule', e.target.value)}
            />
          </motion.div>

          <motion.div 
            className="card"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiTarget} className="text-brand-orange mr-2" />
              <h3 className="font-semibold text-stone-800">Obstacles to Remove</h3>
            </div>
            <textarea
              className="form-textarea"
              placeholder="What might get in your way? How can you prepare? (e.g., distractions, time conflicts, perfectionism)"
              value={formData.obstacles}
              onChange={(e) => handleInputChange('obstacles', e.target.value)}
            />
          </motion.div>
        </div>

        {/* Summary Panel */}
        <div className="lg:sticky lg:top-8">
          <motion.div 
            className="card bg-gradient-to-br from-cream to-brand-blue/10 border-brand-blue/20"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="font-serif text-xl font-bold text-stone-800 mb-6">
              Your Writing Plan Summary
            </h3>
            
            <div className="space-y-4">
              {formData.targetWordCount && (
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-brand-orange/20">
                  <h4 className="font-medium text-stone-800 mb-2">Target Length</h4>
                  <p className="text-stone-600">
                    {formData.targetWordCount === 'custom' 
                      ? `${parseInt(formData.customWordCount || 0).toLocaleString()} words`
                      : `${parseInt(formData.targetWordCount || 0).toLocaleString()} words`
                    }
                  </p>
                </div>
              )}
              
              {formData.timeline && (
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-brand-blue/20">
                  <h4 className="font-medium text-stone-800 mb-2">Timeline</h4>
                  <p className="text-stone-600">
                    {formData.timeline === 'custom' 
                      ? `${formData.customTimeline} days`
                      : `${formData.timeline} days`
                    }
                  </p>
                </div>
              )}
              
              {dailyGoal > 0 && (
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-brand-orange/20">
                  <h4 className="font-medium text-stone-800 mb-2">Daily Word Goal</h4>
                  <p className="text-brand-orange font-semibold text-lg">
                    {dailyGoal.toLocaleString()} words/day
                  </p>
                  <p className="text-stone-500 text-sm">
                    Writing {formData.writingDaysPerWeek} days per week
                  </p>
                </div>
              )}
              
              {wordsPerChapter > 0 && (
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-brand-blue/20">
                  <h4 className="font-medium text-stone-800 mb-2">Words per Chapter</h4>
                  <p className="text-stone-600">
                    ~{wordsPerChapter.toLocaleString()} words
                  </p>
                </div>
              )}
            </div>
            
            {dailyGoal > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 p-4 bg-gradient-to-r from-brand-orange/10 to-brand-blue/10 rounded-xl border border-brand-orange/20"
              >
                <p className="text-brand-blue text-sm font-medium">
                  🎯 Perfect! Your plan is realistic and achievable. Trust God's timing and your commitment!
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Navigation */}
      <motion.div 
        className="flex justify-between mt-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
      >
        <button
          onClick={handleBack}
          className="btn-secondary flex items-center"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2" />
          Back to Structure
        </button>
        
        <button
          onClick={handleContinue}
          className={`flex items-center px-8 py-3 rounded-xl font-medium transition-all duration-300 ${
            isFormComplete 
              ? 'bg-gradient-to-r from-brand-orange to-brand-blue text-white hover:shadow-lg' 
              : 'bg-stone-300 text-stone-500 cursor-not-allowed'
          }`}
          disabled={!isFormComplete}
        >
          Continue to Chapter Kickstart
          <SafeIcon icon={FiArrowRight} className="ml-2" />
        </button>
      </motion.div>
    </motion.div>
  );
};

export default ProjectScope;